/**
 * Animated sticker inspector (STX-PLAN §9.2): the TypeScript twin of the server's Java inspector and the
 * packager's Python one. All three apply ONE contract, so a payload the browser accepts here is the payload
 * the server accepts. The server remains the authority; this gives the admin early, specific feedback
 * ("33 frames · 2.40 s · 326 KB") before the release is created.
 *
 * Container: `RIFF` size + 8 == length, no trailing bytes, `WEBP`; first chunk `VP8X` (size 10) with ANIM and
 * ALPHA set, ICC/EXIF/XMP rejected, reserved bits 0, canvas == the declared size; then `ANIM` (loop == 0, the
 * background is ignored); then only `ANMF`. Per frame: reserved flag bits 0; offset x2, size +1, inside the canvas;
 * duration 20-3000 ms; sub-chunks `ALPH`+`VP8 `, `VP8 ` alone or `VP8L` alone (a present ALPH has reserved 0,
 * preprocessing 0, compression 1); the VP8 keyframe header and the VP8L signature dimensions match the frame.
 *
 * `VP8 ` without `ALPH` is valid: libwebp omits the alpha plane for a fully opaque sub-rectangle, and real
 * deliveries contain such frames (the server lane found 19 of them in 5 of the 64 current deliveries). Rejecting
 * them would make this check stricter than the server. Global: 2-64 frames; total 200-6000 ms; long edge
 * <= 1024, short edge >= 64; W*H*frames <= 512*512*64; bytes <= 1,000,000.
 */

export const ANIMATED_WEBP_LIMITS = {
  maxBytes: 1_000_000,
  minFrames: 2,
  maxFrames: 64,
  minFrameMs: 20,
  maxFrameMs: 3000,
  minTotalMs: 200,
  maxTotalMs: 6000,
  maxLongEdge: 1024,
  minShortEdge: 64,
  maxVolume: 512 * 512 * 64,
} as const;

export interface AnimatedWebpFrame {
  x: number;
  y: number;
  width: number;
  height: number;
  durationMs: number;
  /** Lossy frames carry their alpha in a separate ALPH chunk; lossless (VP8L) frames carry it in the bitstream. */
  codec: 'VP8' | 'VP8L';
  /** False for a lossy frame whose sub-rectangle is fully opaque, and for every lossless frame. */
  hasAlphaChunk: boolean;
}

export interface AnimatedWebpInfo {
  canvasWidth: number;
  canvasHeight: number;
  loopCount: number;
  /** The number of ANMF chunks: what `CGImageSourceGetCount` returns on iOS. */
  frameCount: number;
  /** One loop, in milliseconds. */
  durationMs: number;
  bytes: number;
  frames: AnimatedWebpFrame[];
}

/** The facts a manifest entry declares about its animation; they must equal the file's. */
export interface DeclaredAnimation {
  originalWidth: number;
  originalHeight: number;
  frameCount?: number | null | undefined;
  durationMs?: number | null | undefined;
  loopCount?: number | null | undefined;
}

export class AnimatedWebpError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AnimatedWebpError';
  }
}

const VP8X_ANIMATION = 0x02;
const VP8X_XMP = 0x04;
const VP8X_EXIF = 0x08;
const VP8X_ALPHA = 0x10;
const VP8X_ICC = 0x20;
/** Two reserved bits at the top of the flag byte and the reserved bit at the bottom. */
const VP8X_RESERVED = 0xc1;

/** The ANMF flag byte carries the blend and dispose bits; the other six are reserved. */
const ANMF_RESERVED = 0xfc;

const RIFF_HEADER_BYTES = 12;
const CHUNK_HEADER_BYTES = 8;
const ANMF_HEADER_BYTES = 16;

function reject(message: string): never {
  throw new AnimatedWebpError(message);
}

/** True when the bytes start like a WebP file (`RIFF` .... `WEBP`). */
export function hasWebpSignature(data: Uint8Array): boolean {
  if (data.length < RIFF_HEADER_BYTES) return false;
  const ascii = (offset: number) => String.fromCharCode(...data.subarray(offset, offset + 4));
  return ascii(0) === 'RIFF' && ascii(8) === 'WEBP';
}

class Reader {
  private readonly view: DataView;
  constructor(readonly data: Uint8Array) {
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  }
  has(offset: number, length: number): boolean {
    return offset >= 0 && offset + length <= this.data.length;
  }
  private need(offset: number, length: number) {
    if (!this.has(offset, length)) reject('The file is truncated');
  }
  u8(offset: number): number {
    this.need(offset, 1);
    return this.data[offset]!;
  }
  u16(offset: number): number {
    this.need(offset, 2);
    return this.view.getUint16(offset, true);
  }
  u24(offset: number): number {
    this.need(offset, 3);
    return this.data[offset]! | (this.data[offset + 1]! << 8) | (this.data[offset + 2]! << 16);
  }
  u32(offset: number): number {
    this.need(offset, 4);
    return this.view.getUint32(offset, true);
  }
  fourcc(offset: number): string {
    this.need(offset, 4);
    return String.fromCharCode(...this.data.subarray(offset, offset + 4));
  }
}

interface ChunkHeader {
  fourcc: string;
  /** Offset of the first payload byte. */
  payload: number;
  size: number;
  /** Offset of the next chunk: the payload plus its pad byte when the size is odd. */
  next: number;
}

function chunkAt(reader: Reader, offset: number, limit: number): ChunkHeader {
  if (offset + CHUNK_HEADER_BYTES > limit) reject('The file is truncated');
  const size = reader.u32(offset + 4);
  const payload = offset + CHUNK_HEADER_BYTES;
  const next = payload + size + (size & 1);
  if (next > limit) reject(`Chunk '${reader.fourcc(offset)}' runs past the end of its container`);
  return { fourcc: reader.fourcc(offset), payload, size, next };
}

/** Throws `AnimatedWebpError` with a specific reason unless the bytes satisfy the §9.2 contract. */
export function inspectAnimatedWebp(data: Uint8Array): AnimatedWebpInfo {
  const limits = ANIMATED_WEBP_LIMITS;
  if (data.length > limits.maxBytes)
    reject(`The file is ${data.length} bytes; animated stickers are limited to ${limits.maxBytes}`);
  const reader = new Reader(data);
  if (!hasWebpSignature(data)) reject('Not a WebP file (expected RIFF/WEBP)');
  if (reader.u32(4) + 8 !== data.length)
    reject('The RIFF size does not match the file length (trailing or missing bytes)');

  // VP8X: the extended header carrying the canvas and the feature flags.
  const vp8x = chunkAt(reader, RIFF_HEADER_BYTES, data.length);
  if (vp8x.fourcc !== 'VP8X' || vp8x.size !== 10)
    reject('The first chunk must be VP8X (an animated WebP is an extended-format file)');
  const flags = reader.u8(vp8x.payload);
  if (flags & VP8X_RESERVED || reader.u24(vp8x.payload + 1) !== 0)
    reject('VP8X reserved bits are set');
  if (flags & (VP8X_ICC | VP8X_EXIF | VP8X_XMP))
    reject('ICC, EXIF and XMP metadata are not allowed; strip them before packaging');
  if (!(flags & VP8X_ANIMATION)) reject('The animation flag is not set');
  if (!(flags & VP8X_ALPHA))
    reject('The alpha flag is not set; transparent areas would render black');
  const canvasWidth = reader.u24(vp8x.payload + 4) + 1;
  const canvasHeight = reader.u24(vp8x.payload + 7) + 1;

  // ANIM: global animation parameters.
  const anim = chunkAt(reader, vp8x.next, data.length);
  if (anim.fourcc !== 'ANIM' || anim.size !== 6) reject('The chunk after VP8X must be ANIM');
  const loopCount = reader.u16(anim.payload + 4);
  if (loopCount !== 0) reject(`The loop count is ${loopCount}; stickers must loop forever (0)`);

  // ANMF: one chunk per frame, and nothing else.
  const frames: AnimatedWebpFrame[] = [];
  let totalMs = 0;
  let offset = anim.next;
  while (offset < data.length) {
    const number = frames.length + 1;
    const chunk = chunkAt(reader, offset, data.length);
    if (chunk.fourcc !== 'ANMF')
      reject(`Unexpected chunk '${chunk.fourcc}' (only ANMF frames may follow ANIM)`);
    if (chunk.size < ANMF_HEADER_BYTES + CHUNK_HEADER_BYTES)
      reject(`Frame ${number} has no image data`);
    const frame = readFrame(reader, chunk, number, canvasWidth, canvasHeight);
    frames.push(frame);
    totalMs += frame.durationMs;
    offset = chunk.next;
  }

  if (frames.length < limits.minFrames || frames.length > limits.maxFrames)
    reject(`${frames.length} frames; expected ${limits.minFrames}-${limits.maxFrames}`);
  if (totalMs < limits.minTotalMs || totalMs > limits.maxTotalMs)
    reject(`The loop lasts ${totalMs} ms; expected ${limits.minTotalMs}-${limits.maxTotalMs} ms`);
  if (
    Math.max(canvasWidth, canvasHeight) > limits.maxLongEdge ||
    Math.min(canvasWidth, canvasHeight) < limits.minShortEdge
  )
    reject(
      `The canvas is ${canvasWidth}x${canvasHeight}; the long edge must be at most ${limits.maxLongEdge} and the short edge at least ${limits.minShortEdge}`,
    );
  if (canvasWidth * canvasHeight * frames.length > limits.maxVolume)
    reject(
      `${canvasWidth}x${canvasHeight} x ${frames.length} frames exceeds the decoded-size budget`,
    );

  return {
    canvasWidth,
    canvasHeight,
    loopCount,
    frameCount: frames.length,
    durationMs: totalMs,
    bytes: data.length,
    frames,
  };
}

function readFrame(
  reader: Reader,
  anmf: ChunkHeader,
  number: number,
  canvasWidth: number,
  canvasHeight: number,
): AnimatedWebpFrame {
  const limits = ANIMATED_WEBP_LIMITS;
  const p = anmf.payload;
  const x = reader.u24(p) * 2;
  const y = reader.u24(p + 3) * 2;
  const width = reader.u24(p + 6) + 1;
  const height = reader.u24(p + 9) + 1;
  const durationMs = reader.u24(p + 12);
  if (reader.u8(p + 15) & ANMF_RESERVED) reject(`Frame ${number}: reserved ANMF flag bits are set`);
  if (x + width > canvasWidth || y + height > canvasHeight)
    reject(
      `Frame ${number} (${width}x${height} at ${x},${y}) extends outside the ${canvasWidth}x${canvasHeight} canvas`,
    );
  if (durationMs < limits.minFrameMs || durationMs > limits.maxFrameMs)
    reject(
      `Frame ${number} lasts ${durationMs} ms; expected ${limits.minFrameMs}-${limits.maxFrameMs} ms`,
    );

  const end = anmf.payload + anmf.size;
  let cursor = p + ANMF_HEADER_BYTES;
  let sub = chunkAt(reader, cursor, end);
  let codec: AnimatedWebpFrame['codec'];
  const hasAlphaChunk = sub.fourcc === 'ALPH';
  if (hasAlphaChunk) {
    if (sub.size < 1) reject(`Frame ${number}: empty ALPH chunk`);
    const header = reader.u8(sub.payload);
    if (header >> 6 !== 0) reject(`Frame ${number}: ALPH reserved bits are set`);
    if (((header >> 4) & 3) !== 0)
      reject(
        `Frame ${number}: ALPH pre-processing is ${(header >> 4) & 3}; encode with alpha_quality 100`,
      );
    if ((header & 3) !== 1)
      reject(
        `Frame ${number}: ALPH uses compression ${header & 3}; expected lossless compression (1)`,
      );
    cursor = sub.next;
    sub = chunkAt(reader, cursor, end);
    if (sub.fourcc !== 'VP8 ')
      reject(`Frame ${number}: ALPH must be followed by a lossy VP8 chunk`);
    codec = 'VP8';
  } else if (sub.fourcc === 'VP8 ') {
    codec = 'VP8';
  } else if (sub.fourcc === 'VP8L') {
    codec = 'VP8L';
  } else {
    reject(`Frame ${number}: unexpected chunk '${sub.fourcc}'`);
  }

  if (codec === 'VP8') {
    if (sub.size < 10) reject(`Frame ${number}: VP8 data is truncated`);
    const tag = reader.u24(sub.payload);
    if (tag & 1) reject(`Frame ${number}: VP8 data is not a keyframe`);
    if (
      reader.u8(sub.payload + 3) !== 0x9d ||
      reader.u8(sub.payload + 4) !== 0x01 ||
      reader.u8(sub.payload + 5) !== 0x2a
    )
      reject(`Frame ${number}: VP8 start code is missing`);
    const w = reader.u16(sub.payload + 6) & 0x3fff;
    const h = reader.u16(sub.payload + 8) & 0x3fff;
    if (w !== width || h !== height)
      reject(`Frame ${number}: VP8 bitstream is ${w}x${h} but the frame is ${width}x${height}`);
  } else {
    if (sub.size < 5) reject(`Frame ${number}: VP8L data is truncated`);
    if (reader.u8(sub.payload) !== 0x2f) reject(`Frame ${number}: VP8L signature is missing`);
    const bits = reader.u32(sub.payload + 1);
    const w = (bits & 0x3fff) + 1;
    const h = ((bits >>> 14) & 0x3fff) + 1;
    if (bits >>> 29 !== 0) reject(`Frame ${number}: unsupported VP8L version`);
    if (w !== width || h !== height)
      reject(`Frame ${number}: VP8L bitstream is ${w}x${h} but the frame is ${width}x${height}`);
  }
  if (sub.next !== end) reject(`Frame ${number}: unexpected data after the image chunk`);
  return { x, y, width, height, durationMs, codec, hasAlphaChunk };
}

/** The facts the manifest declares must be the facts of the file (what iOS will read back). */
export function assertAnimationMatches(info: AnimatedWebpInfo, declared: DeclaredAnimation): void {
  if (info.canvasWidth !== declared.originalWidth || info.canvasHeight !== declared.originalHeight)
    reject(
      `The canvas is ${info.canvasWidth}x${info.canvasHeight} but the manifest declares ${declared.originalWidth}x${declared.originalHeight}`,
    );
  if (info.frameCount !== declared.frameCount)
    reject(
      `The file has ${info.frameCount} frames but the manifest declares ${declared.frameCount ?? 'none'}`,
    );
  if (info.durationMs !== declared.durationMs)
    reject(
      `The loop lasts ${info.durationMs} ms but the manifest declares ${declared.durationMs ?? 'none'}`,
    );
  if (info.loopCount !== (declared.loopCount ?? 0))
    reject(
      `The loop count is ${info.loopCount} but the manifest declares ${declared.loopCount ?? 0}`,
    );
}

/** Seconds with the given number of decimals: `2400 -> "2.40"`. */
export function formatSeconds(durationMs: number, digits = 2): string {
  return (durationMs / 1000).toFixed(digits);
}

/** Decimal kilobytes, matching the 1,000,000-byte cap. */
export function formatKilobytes(bytes: number): string {
  return `${Math.round(bytes / 1000)} KB`;
}

/** The feedback line: "33 frames · 2.40 s · 326 KB". */
export function describeAnimation(
  info: Pick<AnimatedWebpInfo, 'frameCount' | 'durationMs' | 'bytes'>,
): string {
  return `${info.frameCount} frames · ${formatSeconds(info.durationMs)} s · ${formatKilobytes(info.bytes)}`;
}
